import React, { useState, useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { MagnifyingGlass, Clock, Eye, ArrowRight, Sparkle, Newspaper, Wrench, Question } from '@phosphor-icons/react';
import { useCart } from '../../context/CartContext';
import { MrgHeader as MagereHeader } from './MrgHeader';
import { MrgFooter as MagereFooter } from './MrgFooter';
import SeoHead from '../../components/SeoHead';

const API = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_STORE_SLUG = 'mrg';
const PINK = '#F9055B';

const CATEGORY_META = {
  conseils: { label: 'Conseils', Icon: BookOpenIcon, color: '#0ea5e9' },
  guides: { label: "Guides d'achat", Icon: Wrench, color: '#10b981' },
  tendances: { label: 'Tendances', Icon: Sparkle, color: '#f59e0b' },
  actualites: { label: 'Actualités', Icon: Newspaper, color: '#8b5cf6' },
  audition: { label: 'Audition', Icon: Question, color: '#ef4444' },
};

function BookOpenIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width={props.size || 16} height={props.size || 16} fill="currentColor"><path d="M21 5a1 1 0 0 0-1-1h-5a4 4 0 0 0-3 1.354A4 4 0 0 0 9 4H4a1 1 0 0 0-1 1v13a1 1 0 0 0 1 1h5.586l1.707 1.707a1 1 0 0 0 1.414 0L14.414 19H20a1 1 0 0 0 1-1V5Z" /></svg>
  );
}

const CategoryBadge = ({ cat, dark = false }) => {
  const meta = CATEGORY_META[cat] || { label: cat, color: PINK };
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10.5px] font-bold uppercase tracking-wider"
      style={{ backgroundColor: dark ? meta.color + 'cc' : meta.color + '15', color: dark ? '#fff' : meta.color, backdropFilter: dark ? 'blur(6px)' : 'none' }}>
      {meta.label}
    </span>
  );
};

const StoreBlogPage = ({ storeSlug: propStoreSlug }) => {
  const { storeSlug: paramStoreSlug } = useParams();
  const storeSlug = propStoreSlug || paramStoreSlug || DEFAULT_STORE_SLUG;
  const standalone = !!propStoreSlug || !paramStoreSlug;
  const [searchParams, setSearchParams] = useSearchParams();
  const [store, setStore] = useState(null);
  const [pages, setPages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [data, setData] = useState({ posts: [], total: 0, pages: 0, filters: {} });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(parseInt(searchParams.get('page')) || 1);
  const [activeCategory, setActiveCategory] = useState(searchParams.get('category') || '');
  const [search, setSearch] = useState('');
  const { setStoreSlug } = useCart();

  const getLink = (path) => (standalone ? path : `/store/${storeSlug}${path}`);

  useEffect(() => { if (storeSlug) setStoreSlug(storeSlug); }, [storeSlug, setStoreSlug]);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/s/${storeSlug}`).then(r => r.json()),
      fetch(`${API}/api/s/${storeSlug}/pages`).then(r => r.json()).catch(() => []),
      fetch(`${API}/api/s/${storeSlug}/categories`).then(r => r.json()).catch(() => []),
    ]).then(([s, p, c]) => { setStore(s); setPages(p || []); setCategories(c || []); }).catch(() => {});
  }, [storeSlug]);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    params.set('page', page);
    params.set('limit', '13');
    if (activeCategory) params.set('category', activeCategory);
    fetch(`${API}/api/s/${storeSlug}/blog?${params}`)
      .then(r => r.json()).then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [storeSlug, page, activeCategory]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (page > 1) params.set('page', page);
    if (activeCategory) params.set('category', activeCategory);
    setSearchParams(params, { replace: true });
  }, [page, activeCategory, setSearchParams]);

  const filteredPosts = search
    ? data.posts.filter(p => p.title.toLowerCase().includes(search.toLowerCase()) || (p.excerpt || '').toLowerCase().includes(search.toLowerCase()))
    : data.posts;
  const [featuredPost, ...restPosts] = filteredPosts;
  const allCategories = data.filters?.categories || ['conseils', 'guides', 'tendances', 'actualites', 'audition'];

  const fmtDate = (s) => {
    if (!s) return '';
    try {
      return new Date(s).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch { return s.slice(0, 10); }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col overflow-x-hidden" data-testid="blog-page" style={{ fontFamily: "'Poppins', sans-serif" }}>
      <SeoHead
        title={`Blog & conseils — ${store?.name || 'MRG Optique & Audition'}`}
        description="Conseils d'opticien et d'audioprothésiste à Istres : choisir ses lunettes, entretien, dernières tendances solaires, guide audition, 100% Santé."
      />
      <MagereHeader store={store} activePage="blog" standalone={standalone} categories={categories} />

      {/* HERO */}
      <section className="relative pt-32 pb-12 md:pb-20 bg-gradient-to-br from-slate-50 via-white to-rose-50/30 border-b border-slate-100">
        <div className="absolute inset-0 opacity-50" style={{ backgroundImage: `radial-gradient(circle at 20% 50%, ${PINK}11 0%, transparent 40%), radial-gradient(circle at 80% 30%, ${PINK}08 0%, transparent 50%)` }} />
        <div className="max-w-[1240px] mx-auto px-6 relative">
          <div className="flex items-baseline gap-3 mb-3">
            <span className="text-[11px] font-bold uppercase tracking-[0.22em]" style={{ color: PINK }}>Le Journal MRG</span>
            <span className="text-[11px] text-slate-400">— {data.total || 0} article{data.total > 1 ? 's' : ''}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.05] tracking-tight max-w-3xl">
            Voir le monde
            <span className="block" style={{ color: PINK }}>autrement.</span>
          </h1>
          <p className="text-slate-600 text-base md:text-lg max-w-2xl mt-6 leading-relaxed">
            Conseils d&apos;opticien, guides d&apos;achat, dernières tendances solaires et audition : profitez de notre expertise pour mieux choisir.
          </p>

          {/* Search + filter */}
          <div className="mt-9 flex flex-col md:flex-row gap-3 max-w-3xl">
            <div className="relative flex-1">
              <MagnifyingGlass size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text" value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Rechercher un article, un conseil…"
                className="w-full pl-11 pr-4 h-12 rounded-full bg-white border border-slate-200 text-[14px] text-slate-700 placeholder:text-slate-400 focus:outline-none focus:border-slate-400 shadow-sm"
                data-testid="blog-search"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => { setActiveCategory(''); setPage(1); }}
                className={`px-4 h-10 rounded-full text-[13px] font-semibold transition-all ${activeCategory === '' ? 'text-white shadow-md' : 'bg-white text-slate-700 border border-slate-200 hover:border-slate-400'}`}
                style={activeCategory === '' ? { backgroundColor: PINK } : {}}
                data-testid="cat-filter-all"
              >
                Tous
              </button>
              {allCategories.slice(0, 4).map(cat => {
                const meta = CATEGORY_META[cat] || { label: cat };
                const active = activeCategory === cat;
                return (
                  <button key={cat} onClick={() => { setActiveCategory(active ? '' : cat); setPage(1); }}
                    className={`px-4 h-10 rounded-full text-[13px] font-semibold transition-all ${active ? 'text-white shadow-md' : 'bg-white text-slate-700 border border-slate-200 hover:border-slate-400'}`}
                    style={active ? { backgroundColor: PINK } : {}}
                    data-testid={`cat-filter-${cat}`}
                  >
                    {meta.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* CONTENT */}
      <main className="flex-1 py-12 md:py-16 bg-white">
        <div className="max-w-[1240px] mx-auto px-6">
          {loading ? (
            <div className="grid md:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[4/3] bg-slate-100 rounded-2xl mb-3" />
                  <div className="h-3 bg-slate-100 rounded w-1/3 mb-2" />
                  <div className="h-5 bg-slate-100 rounded w-3/4 mb-2" />
                  <div className="h-3 bg-slate-100 rounded w-full" />
                </div>
              ))}
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-24">
              <p className="text-2xl font-bold text-slate-700 mb-2">Bientôt en ligne</p>
              <p className="text-sm text-slate-500 mb-6">Nos premiers articles arrivent prochainement. Revenez vite !</p>
              {(activeCategory || search) && (
                <button onClick={() => { setActiveCategory(''); setSearch(''); setPage(1); }}
                  className="px-5 h-10 rounded-full text-white text-[13px] font-semibold" style={{ backgroundColor: PINK }}>
                  Voir tous les articles
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Featured (first article large) */}
              {featuredPost && (
                <Link to={getLink(`/blog/${featuredPost.slug}`)}
                  className="group block mb-12 grid md:grid-cols-[1.4fr_1fr] gap-0 md:gap-8 rounded-3xl overflow-hidden bg-slate-900 text-white shadow-xl border border-slate-100"
                  data-testid="blog-featured">
                  <div className="relative aspect-[16/10] md:aspect-auto md:min-h-[420px] overflow-hidden">
                    {featuredPost.featured_image ? (
                      <img src={featuredPost.featured_image} alt={featuredPost.title}
                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    ) : (
                      <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${PINK}, #111111)` }} />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent md:bg-gradient-to-r md:from-transparent md:via-transparent md:to-slate-900/90" />
                  </div>
                  <div className="p-7 md:p-10 flex flex-col justify-center">
                    <div className="flex items-center gap-2 mb-4">
                      <span className="text-[10px] font-bold uppercase tracking-[0.22em]" style={{ color: PINK }}>À la une</span>
                      <span className="w-8 h-px" style={{ background: PINK }} />
                      <CategoryBadge cat={featuredPost.category} dark />
                    </div>
                    <h2 className="text-2xl md:text-[34px] font-bold leading-[1.15] mb-4 group-hover:text-white/95 transition-colors">
                      {featuredPost.title}
                    </h2>
                    {featuredPost.excerpt && (
                      <p className="text-white/65 text-[14.5px] leading-relaxed line-clamp-3 mb-5">
                        {featuredPost.excerpt}
                      </p>
                    )}
                    <div className="flex items-center gap-4 text-[12px] text-white/45 mb-6">
                      {featuredPost.published_at && <span className="inline-flex items-center gap-1"><Clock size={12} /> {fmtDate(featuredPost.published_at)}</span>}
                      {featuredPost.reading_time && <span>{featuredPost.reading_time} min de lecture</span>}
                      {featuredPost.views > 0 && <span className="inline-flex items-center gap-1"><Eye size={12} /> {featuredPost.views}</span>}
                    </div>
                    <span className="inline-flex w-fit items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-[13px] text-white transition-transform group-hover:scale-105" style={{ backgroundColor: PINK }}>
                      Lire l&apos;article <ArrowRight size={15} weight="bold" />
                    </span>
                  </div>
                </Link>
              )}

              {/* Grid */}
              {restPosts.length > 0 && (
                <>
                  <div className="flex items-baseline justify-between mb-6">
                    <h3 className="text-[20px] font-bold text-slate-900">{activeCategory ? CATEGORY_META[activeCategory]?.label || activeCategory : 'Tous nos articles'}</h3>
                    <p className="text-[12px] text-slate-400">{restPosts.length} article{restPosts.length > 1 ? 's' : ''}</p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-grid">
                    {restPosts.map((post, i) => (
                      <Link key={post.id} to={getLink(`/blog/${post.slug}`)}
                        className="group block bg-white rounded-2xl border border-slate-100 hover:border-slate-300 hover:shadow-lg transition-all overflow-hidden"
                        data-testid={`blog-card-${i}`}
                      >
                        <div className="relative aspect-[16/10] bg-slate-100 overflow-hidden">
                          {post.featured_image ? (
                            <img src={post.featured_image} alt={post.title}
                              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                          ) : (
                            <div className="absolute inset-0 flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${PINK}11, ${PINK}33)` }}>
                              <Newspaper size={36} style={{ color: PINK }} />
                            </div>
                          )}
                          <div className="absolute top-3 left-3"><CategoryBadge cat={post.category} dark /></div>
                        </div>
                        <div className="p-5">
                          <h3 className="text-[15.5px] font-bold text-slate-900 leading-snug line-clamp-2 mb-2 group-hover:text-slate-700">{post.title}</h3>
                          {post.excerpt && <p className="text-[13px] text-slate-500 leading-relaxed line-clamp-2 mb-4">{post.excerpt}</p>}
                          <div className="flex items-center justify-between text-[11.5px] text-slate-400">
                            <span>{fmtDate(post.published_at)}</span>
                            <span className="inline-flex items-center gap-1 font-semibold transition-colors group-hover:text-slate-700">
                              Lire <ArrowRight size={11} weight="bold" />
                            </span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </>
              )}

              {/* Pagination */}
              {data.pages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-12" data-testid="blog-pagination">
                  {[...Array(data.pages)].map((_, i) => (
                    <button key={i} onClick={() => setPage(i + 1)}
                      className={`w-10 h-10 rounded-full text-[13px] font-semibold transition-all ${page === i + 1 ? 'text-white shadow-md' : 'text-slate-600 hover:bg-slate-100 bg-white border border-slate-200'}`}
                      style={page === i + 1 ? { backgroundColor: PINK } : {}}>
                      {i + 1}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </main>

      <MagereFooter store={store} pages={pages} categories={categories} standalone={standalone} />
    </div>
  );
};

export default StoreBlogPage;
